import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import PropertyCard from '../components/PropertyCard.jsx';
import { HeroSearch } from '../components/SearchControls.jsx';
import { EmptyState, Spinner } from '../components/ui.jsx';
import { useFavoriteToggle, useFetch, useTitle } from '../lib/hooks.js';
import { formatXaf, pluralise } from '../lib/format.js';
import { toQuery } from '../api/client.js';
import { useMeta } from '../state/MetaContext.jsx';

const STEPS = [
  {
    title: 'Search where you want to live',
    body: 'Filter by town, neighbourhood, rent, number of rooms and the things that matter, like a private water supply or your own ENEO meter.'
  },
  {
    title: 'Look properly before you travel',
    body: 'Every listing carries photos, and many carry a walk-through video, so you do not waste a trip on a house that was described generously.'
  },
  {
    title: 'Apply online and track the answer',
    body: 'Send your details and documents once. The landlord replies through the platform and you can see exactly where your application stands.'
  }
];

const Home = () => {
  useTitle('Houses to rent across Cameroon');
  const navigate = useNavigate();
  const { data, loading, error, setData } = useFetch('/properties/highlights');
  const { platform } = useMeta();
  const [tab, setTab] = useState('featured');

  const onFavorite = useFavoriteToggle((id, favorited) => {
    setData({
      ...data,
      featured: data.featured.map((item) => (item.id === id ? { ...item, favorited } : item)),
      newest: data.newest.map((item) => (item.id === id ? { ...item, favorited } : item)),
      forSale: (data.forSale || []).map((item) => (item.id === id ? { ...item, favorited } : item))
    });
  });

  const listings = data ? (tab === 'featured' ? data.featured : tab === 'sale' ? data.forSale || [] : data.newest) : [];
  const heroMedia = platform?.heroMedia;

  return (
    <>
      <section className="hero">
        {heroMedia ? (
          <div className="hero-media">
            {heroMedia.type === 'video' ? (
              <video src={heroMedia.url} autoPlay muted loop playsInline />
            ) : (
              <img src={heroMedia.url} alt="" />
            )}
            <div className="hero-media-overlay" />
          </div>
        ) : null}

        <div className="container hero-inner">
          <h1>Find your next house in Cameroon</h1>
          <p className="hero-sub">
            Rooms, flats and whole houses in Tiko, Buea, Limbe, Douala, Yaounde and beyond. Rents in FCFA, no agency fee
            to see a listing, and you deal with the landlord directly.
          </p>

          <HeroSearch onSearch={(params) => navigate(`/search${toQuery(params)}`)} />

          <div className="hero-quick">
            <span>Popular right now:</span>
            {['Tiko', 'Buea', 'Douala', 'Limbe', 'Yaounde'].map((city) => (
              <Link key={city} to={`/search?city=${city}`} className="chip">
                {city}
              </Link>
            ))}
            <Link to="/search?maxPrice=40000" className="chip">
              Under 40 000 FCFA
            </Link>
          </div>
        </div>
      </section>

      {platform?.advertMedia ? (
        <section className="container advert-slot">
          {(() => {
            const media =
              platform.advertMedia.type === 'video' ? (
                <video src={platform.advertMedia.url} autoPlay muted loop playsInline />
              ) : (
                <img src={platform.advertMedia.url} alt="Advertisement" />
              );
            return platform.advertMedia.link ? (
              <a href={platform.advertMedia.link} target="_blank" rel="noopener noreferrer">
                {media}
              </a>
            ) : (
              media
            );
          })()}
        </section>
      ) : null}

      <section className="container section">
        <div className="section-head">
          <div className="tab-row" role="tablist" aria-label="Listing groups">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'featured'}
              className={`tab ${tab === 'featured' ? 'is-current' : ''}`}
              onClick={() => setTab('featured')}
            >
              Most viewed
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'newest'}
              className={`tab ${tab === 'newest' ? 'is-current' : ''}`}
              onClick={() => setTab('newest')}
            >
              Just added
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'sale'}
              className={`tab ${tab === 'sale' ? 'is-current' : ''}`}
              onClick={() => setTab('sale')}
            >
              For sale
            </button>
          </div>
          <Link to={tab === 'sale' ? '/search?kind=sale' : '/search'} className="link-strong">
            {tab === 'sale' ? 'See everything for sale' : 'See all listings'}
          </Link>
        </div>

        {loading ? <Spinner /> : null}
        {error ? <EmptyState title="Listings are not loading" message={error} /> : null}

        {data && listings.length === 0 ? (
          <EmptyState
            title={tab === 'sale' ? 'Nothing for sale yet' : 'No listings published yet'}
            message={
              tab === 'sale'
                ? 'Houses and land put up for sale will show up here.'
                : 'Once landlords publish their first houses they will show up here.'
            }
            action={
              <Link to="/register?role=landlord" className="btn btn-primary">
                {tab === 'sale' ? 'Sell a house or land' : 'List a property'}
              </Link>
            }
          />
        ) : null}

        <div className="card-grid">
          {listings.map((property) => (
            <PropertyCard key={property.id} property={property} onToggleFavorite={onFavorite} />
          ))}
        </div>
      </section>

      {data?.towns?.length ? (
        <section className="container section">
          <div className="section-head">
            <h2>Where people are renting</h2>
          </div>
          <div className="town-grid">
            {data.towns.map((town) => (
              <Link key={town.city} to={`/search?city=${town.city}`} className="town-tile">
                <strong>{town.city}</strong>
                <span>{pluralise(town.listings, 'listing')}</span>
                <em>from {formatXaf(town.fromPrice)}</em>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="section band">
        <div className="container">
          <h2 className="band-title">How renting here works</h2>
          <div className="step-grid">
            {STEPS.map((step, index) => (
              <div key={step.title} className="step">
                <span className="step-number">{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container section">
        <div className="cta-panel">
          <div>
            <h2>Have a house, flat or room to let?</h2>
            <p>
              Put it in front of tenants who are searching for exactly what you have. Add your photos and a short video,
              set your rent, and take applications online instead of by phone at all hours.
            </p>
          </div>
          <Link to="/register?role=landlord" className="btn btn-primary btn-lg">
            List your property
          </Link>
        </div>
      </section>
    </>
  );
};

export default Home;
